import React from 'react';
import { parsePrice } from '@utils/price-helpers';
import { useDispatch } from 'react-redux';
import { deleteItemFromCart } from '@utils/shopify-helpers';
import { LensSkus, PRODUCT_TYPES, TOOLTIP_COPY } from '@utils/enums';
import * as Sentry from '@sentry/browser';
import { deleteItemByKey, setIsLoading } from '@features/cart/cartSlice';
import TrashIconCart from '@components/cart/icons/TrashIconCart';
import LineIcon from '@components/cart/icons/LineIcon';
import styles from './CartItem.module.scss';
import Tooltip from '@components/common/Tooltip';
import { checkForNonRxDiscount } from '../../../utils/shopify-helpers';
import LightBoxButton from '@components/icons/LightBoxButton';
import { toggleIsVisible } from '@utils/ui-utils';
import useWindowDimensions from '@hooks/useWindowDimensions';

const CartItem = ({ item, quantity, image, isBaseFrame, isLens, itemGroupKey, rxType, onDeleteBaseFrame, minicart }) => {
	const dispatch = useDispatch();
	const { width: viewportWidth } = useWindowDimensions();

	let productTitle = item.product_title;
	let itemImage = image ?? item.image;

	const isPackaging = item.product_type === 'PACKAGING';
	const isSunLens = item.sku.includes('SUN_LENS');
	const bundleKey = item.properties._bundle_key;
	const isTopFrame = item.product_type.includes(PRODUCT_TYPES.TOP_FRAME);

	const preOrderSkus = Object.keys(preOrders);
	const messageNeeded = preOrderSkus ? preOrderSkus.find(sku => sku === item.sku) : false;

	// Non-RX Sun Change
	if (isBaseFrame && item.properties._lensImageUrl) itemImage = item.properties._lensImageUrl;
	if (isSunLens) productTitle = `${item.title} Lens${rxType && rxType.toLowerCase() === 'single-vision' ? ` - ${rxType}` : ''}`;

	// we need to account for adding/deleting the Blue Light Upsell for Non-Rx Frames. Does not apply for Sun Lenses
	// NonRx discount is not applied until Item is actually in the Cart, so adding as an upsell will calculate a discount price of $49
	const adjustedPrice = checkForNonRxDiscount(item.handle, rxType === 'Non-Prescription') ? 0 : item.discounted_price;

	const handleOnDeleteItem = async item => {
		try {
			dispatch(setIsLoading({ value: true, key: itemGroupKey }));
			if (item.product_type.includes(PRODUCT_TYPES.BASE_FRAME)) {
				onDeleteBaseFrame();
				return;
			}

			await deleteItemFromCart(item.key, 0);
			dispatch(
				deleteItemByKey({
					itemKey: item.key,
					discountPrice: checkForNonRxDiscount(item.handle, rxType === 'Non-Prescription'),
				})
			);
		} catch (error) {
			Sentry.captureException(error, `HTTP ${error.status} ${error.statusText}: ${error.message}`);
		}
	};

	const trashIcon = !isSunLens ? (
		<>
			<TrashIconCart
				onDelete={() => {
					handleOnDeleteItem(item);
				}}
				dataCart={item.title}
			/>
			<LineIcon />
		</>
	) : null;

	const price = (
		<div className={`button item-price flex-fill align-self-center cart-item__quant item-price-${item.id}`}>
			${parsePrice(adjustedPrice * quantity, 0)}
		</div>
	);
	const variantTitle = item?.variant_title?.split(' / ')[0];

	return (
		<div
			className={`col-12 cart-item ${isBaseFrame && 'cart-item__base'} ${
				isLens ? 'cart-item__lens' : `${styles.itemContainer}`
			} item-${item.key} ${isTopFrame && styles.topFrameContainer}`}
			data-itemkey={item.key}
			data-bundlekey={bundleKey}
			{...(isBaseFrame ? { [`data-base-frame`]: productTitle } : { [`data-top-frame`]: productTitle })}
		>
			{item.product_type !== PRODUCT_TYPES.LENS && (
				<div
					className={isTopFrame ? styles.lightBoxButtonContainer : styles.imageContainer}
					onClick={() => isTopFrame && viewportWidth < 576 && toggleIsVisible(itemImage, productTitle)}
					data-open-lightbox-cart={isTopFrame ? `${productTitle}` : ''}
				>
					<div
						className={`item-image align-self-center cart-item__image flex-shrink-0 bg-transparent h-100 ${styles.imageWrapper}`}
					>
						<img
							className={`img-fluid mx-auto d-block ${!isBaseFrame && 'h-100'}`}
							{...(isPackaging ? { style: 'object-fit: contain;' } : {})}
							src={itemImage}
							data-id={item.id}
							alt={productTitle}
						/>
					</div>
					{isTopFrame && !minicart && (
						<LightBoxButton
							className={styles.lightBoxButton}
							imageSrc={itemImage}
							imageAlt={productTitle}
							imageSubtitle={`for The ${variantTitle}`}
							width='20'
							height='20'
							price={price}
						/>
					)}
				</div>
			)}
			<div className={styles.rightContainer}>
				<div className={`align-self-center text-lg-left text-left cart-item__title ${styles.productTitleContainer}`}>
					<div className={styles.productTitle}>
						<div className='m-0 title cart-title-flex'>
							{productTitle}
							{quantity > 1 ? <div className='button pl-2'>x{quantity}</div> : null}
						</div>
					</div>
					{isBaseFrame ? (
						<div className={styles.variantTitle} data-base-frame-color={variantTitle}>
							<p className='m-0 cart-title-flex'>in {variantTitle}</p>
						</div>
					) : null}
					{isTopFrame ? (
						<div className={styles.variant} data-base-frame-designation={`for-The-${variantTitle}`}>
							<p className='m-0 cart-title-flex'>for The {variantTitle}</p>
						</div>
					) : null}
					{messageNeeded && (
						<div className={styles.messageNeeded}>
							<p className='m-0 cart-title-flex red-text'>{preOrders[messageNeeded]}</p>
						</div>
					)}
				</div>
				{!isPackaging && (
					<div className='d-flex flex-grow-0 align-items-center'>
						{item.sku?.includes(LensSkus.ULTRA_THIN) && <Tooltip message={TOOLTIP_COPY.ULTRA_THIN} />}
						{!item.is_visual && trashIcon}
						{price}
					</div>
				)}
			</div>
		</div>
	);
};

export default CartItem;
